import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import FlashContext from "components/FlashMessages/FlashContext";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Action } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import RoleFullScreenForm from "../FullScreenForm";
import { useRoleFormConfig } from "../FullScreenForm/hooks";
import { FormValues } from "../FullScreenForm/types";
import { GET_ROLE, ROLE_UPDATE } from "../gql";

const SettingsRoleEditPermissions = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { roleId } = useParams<{ roleId: string }>();
  const config = useRoleFormConfig();

  const backUrl = `/settings/role/${roleId}`;

  const navigate = useNavigate();

  const {
    data,
    loading: queryLoading,
    error,
  } = useQuery(GET_ROLE, {
    variables: {
      id: roleId!,
    },
  });

  const initialValues: FormValues = useMemo(() => {
    return {
      name: data?.role.name || "",
      description: data?.role.description || "",
      permissions: config.reduce((acc, next) => {
        return {
          ...acc,
          [next.id]: next.permissions.reduce(
            (acc, next) => ({ ...acc, [next.action]: data?.role.actions.includes(next.action) }),
            {}
          ),
        };
      }, {}),
    };
  }, [config, data?.role.actions, data?.role.description, data?.role.name]);

  const [updateRole, { loading }] = useMutation(ROLE_UPDATE);

  const onSubmit = useCallback(
    (formValues: FormValues) => {
      if (!roleId || !data?.role) {
        return;
      }

      const actions = Object.keys(formValues.permissions).reduce((acc, next) => {
        const value = formValues.permissions[next];

        return [...acc, ...(Object.keys(value) as Action[]).filter((key) => !!value[key])];
      }, [] as Action[]);

      const input = {
        name: data.role.name,
        description: data.role.description,
        Actions: actions,
      };

      updateRole({
        variables: {
          id: roleId,
          input,
        },
      })
        .then(({ data }) => {
          if (data?.roleUpdate.id) {
            navigate(backUrl, { replace: true });
            reportSuccess({ message: "Role permissions have been successfully updated" });
          }
        })
        .catch(onError);
    },
    [updateRole, roleId, data?.role, navigate, reportSuccess, onError, backUrl]
  );

  useTitle(`Settings · Add permissions · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading || !data) {
    return <PageLoading />;
  }

  if (!loading && !data?.role) {
    return <NotFoundPage />;
  }

  return (
    <RoleFullScreenForm
      title="Add permissions"
      saveButtonTitle="Save"
      leaveTitle="Do you want to leave role permissions edition process?"
      analyticsPage={AnalyticsPageOrganization.OrganizationRolePermissions}
      submitLoading={loading}
      onSubmit={onSubmit}
      isFormDataLoading={queryLoading}
      initialValues={initialValues}
      backUrl={backUrl}
      withDetails={false}
    />
  );
};

export default SettingsRoleEditPermissions;
